// src/components/CartContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";

const CartContext = createContext(null);

const LS_KEY = "solarya_cart_v2";
const COOKIE_KEY_OLD = "cartItems";

function readFromLocalStorage() {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeToLocalStorage(items) {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(items || []));
  } catch {}
}

function readOldCookie() {
  try {
    const raw = Cookies.get(COOKIE_KEY_OLD);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Overlay UI state
  const [isOpen, setIsOpen] = useState(false);
  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  // 1) Hidratálás (LS → (fallback) régi cookie), egyszer
  useEffect(() => {
    // Próbáljuk LS-ből
    let initial = readFromLocalStorage();

    // Ha nincs LS-ben, próbáljuk a régi sütit (régi implementációk)
    if (!initial) {
      const legacy = readOldCookie();
      if (legacy) {
        initial = legacy;
        // migráció LS-be
        writeToLocalStorage(legacy);
        // a régi sütit törölhetjük, hogy ne kavarjon
        Cookies.remove(COOKIE_KEY_OLD);
      }
    }

    if (initial) setCartItems(initial);
    setHasLoaded(true);
  }, []);

  // 2) Mentés LS-be (és opcionálisan sütibe is, ha szeretnéd)
  useEffect(() => {
    if (!hasLoaded) return; // ne írjuk felül első renderkor
    writeToLocalStorage(cartItems);
    // opcionális: régi néven sütibe is (ha valahol még ezt olvasnád)
    Cookies.set(COOKIE_KEY_OLD, JSON.stringify(cartItems), { path: "/", expires: 30 });
  }, [cartItems, hasLoaded]);

  // ===== Műveletek =====
  const addToCart = (product, qty = 1) => {
    const q = Math.max(1, Math.min(99, Number(qty) || 1));
    setCartItems((prev) => {
      const idx = prev.findIndex((i) => i.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(99, (next[idx].quantity || 1) + q) };
        return next;
      }
      return [...prev, { ...product, quantity: q }];
    });
  };

  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setCartItems([]);

  const setQuantity = (id, q) =>
    setCartItems((prev) =>
      prev.map((i) =>
        i.id === id
          ? { ...i, quantity: Math.max(1, Math.min(99, Number(q) || 1)) }
          : i
      )
    );

  const inc = (id) =>
    setCartItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, quantity: Math.min(99, (i.quantity || 1) + 1) } : i
      )
    );

  const dec = (id) =>
    setCartItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, quantity: Math.max(1, (i.quantity || 1) - 1) } : i
      )
    );

  const total = useMemo(
    () =>
      cartItems.reduce(
        (sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity) || 1),
        0
      ),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      setQuantity,
      inc,
      dec,
      total,
      isReady: hasLoaded,
      // overlay
      isOpen,
      openCart,
      closeCart,
    }),
    [cartItems, hasLoaded, total, isOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
